// src/components/demo/DemoResetButton.jsx
//
// Two-variant button that drives the full demo reset:
//   1. POST /api/demo/reset  → backend wipes + reseeds the demo org and
//      flips onboarding rows back to status='active'
//   2. clearOnboardingStorage() → wipes kwery_onboarding_* and
//      kwery_kira_prefill from this browser
//
// Variants:
//   variant="card"   — dashboard-card style (used in AdminDashboardPage)
//   variant="button" — compact inline button (used in superadmin pages)
//
// State machine: idle → confirming → loading → success | error → idle

import React, { useState } from "react";
import { clearOnboardingStorage } from "../../utils/clearDemoStorage";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

export default function DemoResetButton({ variant = "card" }) {
  const [state, setState]   = useState("idle");
  const [result, setResult] = useState(null);

  const handleClick = () => {
    if (state === "idle") setState("confirming");
  };

  const handleCancel = () => setState("idle");

  const handleConfirm = async () => {
    setState("loading");
    try {
      const res = await fetch(`${API}/api/demo/reset`, {
        method: "POST", headers: authH(),
      });
      // Parse — the endpoint surfaces both success (200) and seed-error (500)
      // payloads with the new clear_localStorage_keys field, so either is fine.
      let data = {};
      try { data = await res.json(); } catch {}

      // Always clear browser keys, even when the backend seed path errors —
      // the onboarding flip still ran in its `finally` block server-side.
      const cleared = clearOnboardingStorage();

      const detail = data?.detail || data || {};
      setResult({
        seeded: data?.counts?.onboarding_reactivated ?? detail?.onboarding_reactivated ?? "✓",
        cleared: cleared.length,
        warning: !res.ok ? (detail?.note || "Seed pipeline reported an error") : null,
      });
      setState("success");
      setTimeout(() => { setState("idle"); setResult(null); }, 4000);
    } catch (e) {
      setResult({ error: e?.message || "Network error" });
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  };

  // ────────────────────────────────────────────────────
  //  CARD VARIANT
  // ────────────────────────────────────────────────────
  if (variant === "card") {
    return (
      <div style={{
        background: "#0a0a0a",
        border: state === "confirming"
          ? "0.5px solid rgba(226,75,74,0.4)"
          : "0.5px solid #1a1a1a",
        borderRadius: 12,
        padding: "16px 18px",
        transition: "border-color 0.2s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: state === "confirming" ? "rgba(226,75,74,0.1)" : "rgba(255,255,255,0.04)",
            border: "0.5px solid",
            borderColor: state === "confirming" ? "rgba(226,75,74,0.2)" : "#1a1a1a",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, transition: "all 0.2s",
          }}>
            {state === "loading" ? "⟳"
              : state === "success" ? "✓"
              : state === "error"   ? "✗"
              : "🔄"}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>Reset Demo Data</div>
            <div style={{ fontSize: 11, color: "#444" }}>Re-seeds demo users and clears onboarding</div>
          </div>
        </div>

        {state === "idle" && (
          <button
            type="button"
            onClick={handleClick}
            style={{
              width: "100%", padding: "8px 0",
              background: "transparent", border: "0.5px solid #222",
              borderRadius: 8, color: "#666", fontSize: 12, cursor: "pointer",
              marginTop: 4, transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#333"; e.currentTarget.style.color = "#888"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#222"; e.currentTarget.style.color = "#666"; }}
          >
            Reset demo →
          </button>
        )}

        {state === "confirming" && (
          <div>
            <p style={{ fontSize: 12, color: "#E24B4A", marginBottom: 10, lineHeight: 1.5 }}>
              This will re-seed all demo data and clear onboarding progress. Continue?
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={handleConfirm} style={{
                flex: 1, padding: "8px 0",
                background: "rgba(226,75,74,0.1)",
                border: "0.5px solid rgba(226,75,74,0.3)",
                borderRadius: 8, color: "#E24B4A",
                fontSize: 12, cursor: "pointer", fontWeight: 500,
              }}>Yes, reset</button>
              <button type="button" onClick={handleCancel} style={{
                flex: 1, padding: "8px 0",
                background: "transparent",
                border: "0.5px solid #1a1a1a",
                borderRadius: 8, color: "#555",
                fontSize: 12, cursor: "pointer",
              }}>Cancel</button>
            </div>
          </div>
        )}

        {state === "loading" && (
          <div style={{ textAlign: "center", padding: "8px 0", fontSize: 12, color: "#555" }}>
            Resetting demo data…
          </div>
        )}

        {state === "success" && (
          <div style={{
            background: "rgba(0,255,153,0.04)",
            border: "0.5px solid rgba(0,255,153,0.15)",
            borderRadius: 8, padding: "10px 12px",
            fontSize: 11, color: "#00FF99", lineHeight: 1.6,
          }}>
            ✓ Demo reset complete
            <br />
            <span style={{ color: "#555" }}>
              {result?.cleared || 0} browser key{result?.cleared === 1 ? "" : "s"} cleared · Login as Aisha to see fresh onboarding
            </span>
            {result?.warning && (
              <>
                <br />
                <span style={{ color: "#a78bfa" }}>Note: {result.warning}</span>
              </>
            )}
          </div>
        )}

        {state === "error" && (
          <div style={{
            background: "rgba(226,75,74,0.06)",
            border: "0.5px solid rgba(226,75,74,0.2)",
            borderRadius: 8, padding: "10px 12px",
            fontSize: 11, color: "#E24B4A",
          }}>
            Reset failed — {result?.error || "try again"}
          </div>
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────
  //  BUTTON VARIANT
  // ────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {state === "idle" && (
        <button type="button" onClick={handleClick} style={{
          padding: "8px 16px", background: "transparent",
          border: "0.5px solid #222", borderRadius: 8,
          color: "#888", fontSize: 12, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 6,
          transition: "border-color 0.15s, color 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#333"; e.currentTarget.style.color = "#bbb"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#222"; e.currentTarget.style.color = "#888"; }}
        >
          🔄 Reset demo
        </button>
      )}

      {state === "confirming" && (
        <>
          <span style={{ fontSize: 12, color: "#E24B4A" }}>Sure?</span>
          <button type="button" onClick={handleConfirm} style={{
            padding: "7px 14px",
            background: "rgba(226,75,74,0.1)",
            border: "0.5px solid rgba(226,75,74,0.3)",
            borderRadius: 8, color: "#E24B4A",
            fontSize: 12, cursor: "pointer", fontWeight: 500,
          }}>Yes, reset</button>
          <button type="button" onClick={handleCancel} style={{
            padding: "7px 14px",
            background: "transparent",
            border: "0.5px solid #1a1a1a",
            borderRadius: 8, color: "#555",
            fontSize: 12, cursor: "pointer",
          }}>Cancel</button>
        </>
      )}

      {state === "loading" && (
        <span style={{ fontSize: 12, color: "#555" }}>Resetting…</span>
      )}

      {state === "success" && (
        <span style={{ fontSize: 12, color: "#00FF99" }}>
          ✓ Reset complete — {result?.cleared || 0} key{result?.cleared === 1 ? "" : "s"} cleared
        </span>
      )}

      {state === "error" && (
        <span style={{ fontSize: 12, color: "#E24B4A" }}>
          ✗ Failed — {result?.error || "try again"}
        </span>
      )}
    </div>
  );
}
